/**
 * MobileLayout Component
 * Responsive layout wrapper for the new mobile-friendly app routes
 * Converted from MUI to Tailwind CSS
 */

import { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  Bars3Icon,
  ClockIcon,
  UserIcon,
  ArrowLeftStartOnRectangleIcon,
  SunIcon,
  MoonIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  BookOpenIcon,
  PhoneIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useOathAuth } from '../../services/oathAuthContext';
import { useOathSettings } from '../../services/oathSettingsContext';
import { SettingKeys } from '../../services/oathUserSettingsService';
import { OathStorageKeys } from '../../models/userModels';
import MobileDrawer from './MobileDrawer';
import SessionTimeoutWarningModal from './SessionTimeoutWarningModal';
import AnnouncementsButton from './GlobalMessagesButton';
import ContactUsModal from './ContactUsModal';
import { checkSplashScreen } from '../../services/oathSplashScreenService';

const isDev = process.env.REACT_APP_ENVIRONMENT === 'dev';

/**
 * MobileLayout - Main layout wrapper for /app/* routes
 */
const MobileLayout = () => {
  const navigate = useNavigate();
  const { user, logout, refreshSession, sessionTimeRemaining, isImpersonation, impersonatedBy } = useOathAuth();
  const { isDarkMode, updateSetting } = useOathSettings();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [contactUsOpen, setContactUsOpen] = useState(false);

  // Format remaining time - compact on mobile (MM:SS), full on desktop (HH:MM:SS)
  const formatTime = (ms, compact = false) => {
    if (ms <= 0) return compact ? '00:00' : '00:00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (compact) {
      // Show hours only if > 0, otherwise just MM:SS
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}`;
      }
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Get clock color based on remaining time
  const getClockColorClass = (ms) => {
    const minutes = ms / 60000;
    if (minutes <= 10) return 'text-error border-error';
    if (minutes <= 30) return 'text-warning border-warning';
    if (minutes <= 60) return 'text-secondary border-secondary';
    return 'text-primary border-primary';
  };

  // Poll for active splash screen — force logout if maintenance is active
  useEffect(() => {
    const pollSplash = async () => {
      try {
        const result = await checkSplashScreen('p');
        if (result.active) {
          logout();
          navigate('/');
        }
      } catch (err) {
        // Silently ignore — don't block user on network errors
      }
    };

    // Check immediately on mount, then every 30 seconds
    pollSplash();
    const interval = setInterval(pollSplash, 30000);
    return () => clearInterval(interval);
  }, [logout, navigate]);

  // Listen for session warning events
  useEffect(() => {
    const handleSessionWarning = (event) => {
      const { minutesRemaining } = event.detail;
      toast.warning(
        `Your session will expire in ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''}. Click the clock icon to extend.`,
        { autoClose: 10000 }
      );
    };

    window.addEventListener('oath-session-warning', handleSessionWarning);
    return () => {
      window.removeEventListener('oath-session-warning', handleSessionWarning);
    };
  }, []);

  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => setDrawerOpen(false);

  const handleRefreshSession = async () => {
    const result = await refreshSession();
    if (result.success) {
      toast.success('Session extended successfully');
    } else {
      toast.error('Failed to extend session');
    }
  };

  const handleLogout = () => {
    // Switch to light mode locally without updating user settings
    document.documentElement.classList.remove('dark');
    logout();
    navigate('/');
  };

  const handleProfile = () => {
    navigate('/app/profile');
  };

  const handleToggleDarkMode = async () => {
    const result = await updateSetting(SettingKeys.DARK_MODE, !isDarkMode);
    if (!result.success) {
      toast.error('Failed to update theme');
    }
  };

  const handleDashboard = () => {
    navigate('/app/dashboard');
  };

  const userInitials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()
    : '?';

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-primary-700 dark:bg-gray-950">
      {/* App Bar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 shadow-md dark:shadow-gray-950/50">
        {/* Accent bar - blue in dev, orange in prod */}
        <div className={`h-1 bg-gradient-to-r ${isDev ? 'from-sky-400 via-sky-500 to-sky-400' : 'from-orange-400 via-orange-500 to-orange-400'}`} />
        <div className="flex items-center h-11 sm:h-[52px] px-1 sm:px-4 border-b border-gray-100 dark:border-gray-800">
          {/* Menu Button */}
          <button
            onClick={handleDrawerOpen}
            className="p-1.5 sm:p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Open menu"
          >
            <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>

          {/* Logo */}
          <img
            src="/bankers-surety-logo.png"
            alt="Bankers Surety"
            className="h-5 sm:h-8 ml-0.5 sm:ml-2 cursor-pointer dark:brightness-110"
            onClick={handleDashboard}
          />

          {/* Title - hidden on small screens */}
          <div className="hidden md:flex items-center ml-3">
            <div className="w-px h-6 bg-gray-300 dark:bg-gray-700 mr-3" />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-widest">
              Agency Portal
            </span>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Session Timer — hidden during impersonation (banner shows time instead) */}
          {!isImpersonation && (
            <button
              onClick={handleRefreshSession}
              className={`group flex items-center gap-0.5 sm:gap-1.5 px-1 sm:px-2 py-0.5 sm:py-1 rounded border text-[10px] sm:text-xs font-mono font-semibold transition-all hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-sm active:scale-95 ${getClockColorClass(sessionTimeRemaining)}`}
              title="Click to extend session"
            >
              <div className="relative h-3.5 w-3.5 sm:h-4 sm:w-4">
                <ClockIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 transition-opacity group-hover:opacity-0" />
                <ArrowPathIcon className="absolute inset-0 h-3.5 w-3.5 sm:h-4 sm:w-4 opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
              <span className="sm:hidden">{formatTime(sessionTimeRemaining, true)}</span>
              <span className="hidden sm:inline">{formatTime(sessionTimeRemaining)}</span>
            </button>
          )}

          {/* Home — hidden on mobile (the drawer menu's Dashboard entry covers this) */}
          <button
            onClick={() => navigate('/app/dashboard')}
            className="hidden sm:inline-flex items-center justify-center ml-0.5 sm:ml-2 p-1 sm:p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-primary dark:hover:text-primary-light hover:bg-gray-100 dark:hover:bg-gray-800 transition-all active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary/30"
            aria-label="Dashboard"
            title="Dashboard"
          >
            <HomeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>

          {/* Contact Us */}
          <button
            onClick={() => setContactUsOpen(true)}
            className="ml-0.5 sm:ml-2 p-1 sm:p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-primary dark:hover:text-primary-light hover:bg-gray-100 dark:hover:bg-gray-800 transition-all active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary/30"
            aria-label="Contact Us"
            title="Contact Us"
          >
            <PhoneIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>

          {/* Global Messages */}
          <AnnouncementsButton />

          {/* Resource Center */}
          <button
            onClick={() => navigate('/app/resources')}
            className="ml-0.5 sm:ml-2 p-1 sm:p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-primary dark:hover:text-primary-light hover:bg-gray-100 dark:hover:bg-gray-800 transition-all active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary/30"
            aria-label="Resource Center"
            title="Resource Center"
          >
            <BookOpenIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>

          {/* User Menu */}
          <Menu as="div" className="relative ml-0.5 sm:ml-4">
            <MenuButton className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-primary to-primary-dark text-white text-xs sm:text-sm font-semibold shadow-md shadow-primary/20 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-900 hover:shadow-lg transition-shadow">
              {userInitials}
            </MenuButton>

            <MenuItems
              transition
              className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl ring-1 ring-black/5 dark:ring-gray-700 focus:outline-none z-50 origin-top-right transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 overflow-hidden"
            >
              {/* Orange accent bar */}
              <div className="h-1 bg-gradient-to-r from-primary via-primary to-orange-500" />
              {/* User Info */}
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  @{user?.userName}
                </p>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <MenuItem>
                  <button
                    onClick={handleProfile}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-700"
                  >
                    <UserIcon className="h-4 w-4 mr-3 text-gray-400" />
                    Profile
                  </button>
                </MenuItem>
                <MenuItem>
                  <button
                    onClick={handleToggleDarkMode}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-700"
                  >
                    {isDarkMode ? (
                      <SunIcon className="h-4 w-4 mr-3 text-yellow-500" />
                    ) : (
                      <MoonIcon className="h-4 w-4 mr-3 text-gray-400" />
                    )}
                    {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                  </button>
                </MenuItem>
              </div>

              <div className="border-t border-gray-100 dark:border-gray-700">
                <MenuItem>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-error data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-700"
                  >
                    <ArrowLeftStartOnRectangleIcon className="h-4 w-4 mr-3" />
                    Sign Out
                  </button>
                </MenuItem>
              </div>
            </MenuItems>
          </Menu>
        </div>
      </header>

      {/* Drawer */}
      <MobileDrawer open={drawerOpen} onClose={handleDrawerClose} />

      {/* Session Timeout Warning Modal */}
      <SessionTimeoutWarningModal />

      {/* Contact Us Modal */}
      <ContactUsModal isOpen={contactUsOpen} onClose={() => setContactUsOpen(false)} />

      {/* Impersonation Banner */}
      {isImpersonation && (
        <div className="fixed top-12 sm:top-[57px] left-0 right-0 z-40 bg-amber-100 dark:bg-amber-900/80 border-b border-amber-300 dark:border-amber-700 px-4 py-1.5 text-center">
          <span className="text-xs sm:text-sm font-semibold text-amber-800 dark:text-amber-100">
            Viewing as {user?.loginId || user?.userName} — Impersonation session ({formatTime(sessionTimeRemaining, true)} remaining)
          </span>
        </div>
      )}

      {/* Main Content - offset for header height (accent bar + nav bar) + optional impersonation banner */}
      <main className={`flex-1 min-h-0 ${isImpersonation ? 'pt-[76px] sm:pt-[89px]' : 'pt-12 sm:pt-[57px]'} overflow-y-auto flex flex-col [&>*]:grow [&>*]:shrink-0`}>
        <Outlet />

      </main>

      {/* Dev environment indicator */}
      {isDev && (
        <div className="fixed bottom-4 left-4 z-50 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-sky-500 text-white shadow-lg shadow-sky-500/30 text-[11px] font-semibold">
          <span>DEV</span>
          <div className="w-px h-3 bg-white/30" />
          <button
            type="button"
            onClick={() => {
              const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
              if (token) {
                navigator.clipboard.writeText(token);
                toast.success('Bearer token copied to clipboard');
              } else {
                toast.error('No token found');
              }
            }}
            className="flex items-center gap-1 hover:text-sky-100 transition-colors"
            title="Copy bearer token"
          >
            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MobileLayout;
